/* import __COLOCATED_TEMPLATE__ from './site-header.hbs'; */
import Component from '@glimmer/component';
import { action, getProperties } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import {
  ADMIN_ROUTE_PARENT_NAME__SUB_NAV_MODEL_MAP,
  SELLER_ROUTE_PARENT_NAME__SUB_NAV_MODEL_MAP,
  BUYER_ROUTE_PARENT_NAME__SUB_NAV_MODEL_MAP,
  ADMIN_ROUTE_PARENT_NAMES,
  SELLER_ROUTE_PARENT_NAMES,
  BUYER_ROUTE_PARENT_NAMES,
} from 'sh-www/constants/site-header';
import { PAYOUT_ACCOUNT } from 'sh-www/models/payout-account';
import { HYPERWALLET_USER } from 'sh-www/models/hyperwallet-user';
import AdminAccountingNav from 'sh-www/components/nav/admin-accounting-nav';
import GenericMenu from 'sh-www/components/nav/generic-menu';

import Config from 'sh-www/config/environment';
import clsx from 'clsx';

const PAYOUT_SETUP_ISSUES = {
  NONE: 'none',
  REQUIRED: 'required',
  INCOMPLETE: 'incomplete',
  UNDER_REVIEW: 'under-review',
};

class SiteHeaderComponent extends Component {
  @tracked isSideNavOpen = false;

  @service('state-manager-hybrids/heartbeat-state-manager-hybrid')
  heartbeatStateManager;

  @service
  router;

  @service
  constants;

  @service
  session;

  get PAYOUT_SETUP_ISSUES() {
    return PAYOUT_SETUP_ISSUES;
  }

  get currentUser() {
    return this.session.currentUser;
  }

  get isDemoAllowed() {
    return Config.ALLOW_DEMO;
  }

  get classNames() {
    return clsx(
      this.args.class,
      'navbar-fixed js-app-header SH-c-site-header',
      this.subNavModel && 'with-subnav',
    );
  }

  /**
   * Calculates the current top nav route item
   */
  get headerRouteParentName() {
    const { currentRouteName } = this.router;
    const { currentUser } = this.session;

    if (currentUser.isAdmin) {
      if (currentRouteName.startsWith('admin.accounting')) {
        return ADMIN_ROUTE_PARENT_NAMES.ACCOUNTING;
      }
    }

    if (currentUser.isBuyer) {
      if (currentRouteName.startsWith('posting') || currentRouteName.startsWith('project')) {
        return BUYER_ROUTE_PARENT_NAMES.STORIES;
      } else if (currentRouteName.startsWith('people')) {
        return BUYER_ROUTE_PARENT_NAMES.PEOPLE;
      } else if (currentRouteName.startsWith('message')) {
        return BUYER_ROUTE_PARENT_NAMES.MESSAGES;
      } else if (currentRouteName.startsWith('report')) {
        return BUYER_ROUTE_PARENT_NAMES.REPORTS;
      }
    }

    if (currentUser.isSeller) {
      if (currentRouteName.startsWith('posting')) {
        return SELLER_ROUTE_PARENT_NAMES.POSTINGS;
      } else if (currentRouteName.startsWith('project')) {
        return SELLER_ROUTE_PARENT_NAMES.PROJECTS;
      } else if (currentRouteName.startsWith('message')) {
        return SELLER_ROUTE_PARENT_NAMES.MESSAGES;
      } else if (currentRouteName.startsWith('report')) {
        return SELLER_ROUTE_PARENT_NAMES.REPORTS;
      }
    }

    return null;
  }

  get showSubNav() {
    return Boolean(!this.args.maintenanceModeActive && this.subNavModel);
  }

  get subNavTemplate() {
    const { currentUser } = this.session;

    if (currentUser.isAdmin && this.headerRouteParentName === ADMIN_ROUTE_PARENT_NAMES.ACCOUNTING) {
      return AdminAccountingNav;
    }

    return GenericMenu;
  }

  /**
   * Calculates the appropriate sub-nav model (the items that appear in the subnav bar, if any) for
   * the current route, based on the user's type.
   */
  get subNavModel() {
    const { currentUser } = this.session;
    const { currentRouteName } = this.router;

    let map = {};

    if (!this.headerRouteParentName) {
      return null;
    }

    if (currentUser.isAdmin) {
      map = ADMIN_ROUTE_PARENT_NAME__SUB_NAV_MODEL_MAP;
    } else if (currentUser.isBuyer) {
        if (currentRouteName.startsWith('posting.') || currentRouteName.startsWith('project-details')) {
          return null;
        } else {
          map = BUYER_ROUTE_PARENT_NAME__SUB_NAV_MODEL_MAP;
        }
    } else {
      map = SELLER_ROUTE_PARENT_NAME__SUB_NAV_MODEL_MAP;
    }

    return map[this.headerRouteParentName];
  }

  get underReview() {
    return Boolean(this.session.currentUser?.underReview);
  }

  get showBuyerCreditCard() {
    const { currentUser } = this.session;

    return currentUser
      ? currentUser.isBuyer && Boolean(currentUser.company?.isNeedsCreditCard)
      : false;
  }

  get showPayoutSetupIssue() {
    if (!this.session.currentUser?.isSeller) {
      return false;
    }

    const { currentRouteName } = this.router;

    return currentRouteName
      ? !currentRouteName.startsWith('settings.payout')
      : false;
  }

  get payoutSetupIssue() {
    const { currentUser } = this.session;

    if (!currentUser?.isSeller) {
      return PAYOUT_SETUP_ISSUES.NONE;
    }

    const { payoutAccountRequiresAttention, hyperwalletStatus } = getProperties(this.heartbeatStateManager, [
      'payoutAccountRequiresAttention',
      'hyperwalletStatus',
    ]);

    const { payoutAccounts } = currentUser;
    const preferredAccount = payoutAccounts.find((account) => account.preferred);

    if (!payoutAccountRequiresAttention) {
      const linkedPayoutAccounts = payoutAccounts
        .find((account) => account.status !== PAYOUT_ACCOUNT.STATUSES.UNLINKED);

      if (!linkedPayoutAccounts) {
        return PAYOUT_SETUP_ISSUES.NONE;
      }

      if (preferredAccount?.accountType === PAYOUT_ACCOUNT.ACCOUNT_TYPES.HYPERWALLET) {
        if (hyperwalletStatus === HYPERWALLET_USER.VERIFICATION_STATUSES.UNVERIFIED) {
          return PAYOUT_SETUP_ISSUES.INCOMPLETE;
        }

        if (hyperwalletStatus === HYPERWALLET_USER.VERIFICATION_STATUSES.PENDING) {
          return PAYOUT_SETUP_ISSUES.UNDER_REVIEW;
        }
      }

      return PAYOUT_SETUP_ISSUES.NONE;
    }

    if (!preferredAccount) {
      return PAYOUT_SETUP_ISSUES.REQUIRED;
    }

    if (preferredAccount.status !== PAYOUT_ACCOUNT.STATUSES.ACTIVE) {
      return PAYOUT_SETUP_ISSUES.INCOMPLETE;
    }

    if (preferredAccount.accountType === PAYOUT_ACCOUNT.ACCOUNT_TYPES.HYPERWALLET) {
      if (hyperwalletStatus === HYPERWALLET_USER.VERIFICATION_STATUSES.UNVERIFIED) {
        return PAYOUT_SETUP_ISSUES.INCOMPLETE;
      }

      if (hyperwalletStatus === HYPERWALLET_USER.VERIFICATION_STATUSES.PENDING) {
        return PAYOUT_SETUP_ISSUES.UNDER_REVIEW;
      }
    }

    return PAYOUT_SETUP_ISSUES.NONE;
  }

  get showMessagesToBuyer() {
    const { currentUser } = this.session;

    return currentUser.company
      ? !currentUser.company.isNeedsCreditCard
      : false;
  }

  get showReportsItemToBuyer() {
    const { currentUser } = this.session;

    /**
     * If the company can't view reports, show the Reports item to all its buyers so that they can
     * each see the upsell message.
     */
    if (!currentUser.company?.canUseReports) {
      return true;
    }

    /**
     * If the company can view reports, only show the Reports item to buyers that have access to
     * that information (e.g. admins).
     */
    return currentUser.canViewReports;
  }

  @action
  openSideNav() {
    this.isSideNavOpen = true;
  }

  @action
  closeSideNav() {
    this.isSideNavOpen = false;
  }

  @action
  postAssignmentClicked(e) {
    const company = this.session.currentUser?.company;

    if (!company) {
      return;
    }
  }
}

export default SiteHeaderComponent;
